import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { formatISO } from 'date-fns'

import { Fetch } from '../../../../function/Fetch'

import { Button } from '../../../../components/ui-elements/Button'
import { CheckBox } from '../../../../components/ui-elements/CheckBox'
import { FormProps, FormContainerProps, FormType, CustomPageItemType } from './Form.types'
import {
  TextInputField,
  TextareaField,
  NameInputField,
  SingleSelectField,
  RadioInputField,
  NumberInputField,
  DateInputField,
  MonthInputField,
  MultiSelectField,
} from '../../../../components/ui-parts/Form'

const REGEX = new RegExp('^\\d{4}\\d{2}\\d{2}\\d{4}$')

export const Form: React.FC<FormContainerProps> = (props) => {
  const { bridalFairDetailKey, bridalFairPageKey, startAt } = useParams() as {
    bridalFairDetailKey: string
    bridalFairPageKey?: string
    startAt: string
  }
  const navigate = useNavigate()

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormType>({
    defaultValues: { fairTime: REGEX.test(startAt) ? startAt : undefined },
  })
  const onSubmit = handleSubmit(async (data: FormType) => {
    const body = {
      last_name: data.lastName,
      first_name: data.firstName,
      email: data.email,
      is_agree_terms: data.agreeTerms,
      reservation_infos_attributes: props.bridalFairPageItems
        .filter((pageItem): pageItem is CustomPageItemType => pageItem.fieldType === 'reservation_item')
        .map((pageItem) => {
          const inputValue = data.reservationInfos?.[pageItem.reservationItem.id]
          return {
            reservation_item_id: pageItem.reservationItem.id,
            name: pageItem.name,
            order: pageItem.order,
            item_key: pageItem.reservationItem.itemKey,
            input_value: inputValue instanceof Date ? formatISO(inputValue) : inputValue,
          }
        }),
    }
    if (props.bridalFairPageItems.some((item) => item.fieldType === 'name_kana')) {
      body['last_name_kana'] = data.lastNameKana
      body['first_name_kana'] = data.firstNameKana
    }
    if (props.bridalFairPageItems.some((item) => item.fieldType === 'gender')) {
      body['gender'] = data.gender
    }
    if (props.bridalFairPageItems.some((item) => item.fieldType === 'phone_number')) {
      body['phone_number'] = data.phoneNumber
    }
    if (props.bridalFairPageItems.some((item) => item.fieldType === 'number_of_people')) {
      body['number_of_people'] = data.numberOfPeople
    }
    if (props.bridalFairPageItems.some((item) => item.fieldType === 'content')) {
      body['content'] = data.content
    }
    const res = await Fetch(
      `/customer/api/v1/bridal_fair_details/${bridalFairDetailKey}/fair_times/${data.fairTime}/fair_participations`,
      'POST',
      {
        fair_participation: body,
      },
    )

    if (res.ok) {
      if (bridalFairPageKey) {
        navigate(`/customer/public/bridal_fair_pages/${bridalFairPageKey}/thanks_participation`)
      } else {
        navigate(`/customer/public/bridal_fair_details/${bridalFairDetailKey}/thanks_participation`)
      }
      return
    } else {
      const errorData = await res.json()
      console.error('Error:', errorData)
      alert('予約できませんでした。時間枠を変更して再度予約をお願いします。')
    }
  })
  return (
    <StyledComponent
      {...props}
      register={register}
      control={control}
      onSubmit={onSubmit}
      errors={errors}
      disabled={props.disabled || isSubmitting}
    />
  )
}

const Component: React.FC<FormProps> = ({
  className,
  primaryColor,
  register,
  control,
  onSubmit,
  errors,
  disabled,
  privacyPolicyLink,
  bridalFairPageItems,
  fairTimeOptions,
}) => {
  const [isCheckedAgreement, setIsCheckedAgreement] = useState(false)
  return (
    <form className={`${className} form`} onSubmit={onSubmit}>
      <SingleSelectField
        label="予約時間"
        required
        control={control}
        name="fairTime"
        options={fairTimeOptions}
        error={errors['fairTime']}
        isSearchable={false}
      />
      {bridalFairPageItems.map((item, index) => {
        const fieldProps = {
          key: `${item.fieldType}.${item.order}.${index}`,
          label: item.name,
          placeholder: item.placeholder,
          required: item.isRequired,
          register,
          control,
          disabled,
        }
        switch (item.fieldType) {
          case 'name':
            return (
              <NameInputField
                {...fieldProps}
                placeholders={{ lastName: '姓', firstName: '名' }}
                errors={errors}
                required
              />
            )
          case 'name_kana':
            return (
              <NameInputField
                {...fieldProps}
                names={{ lastName: 'lastNameKana', firstName: 'firstNameKana' }}
                placeholders={{ lastName: 'せい', firstName: 'めい' }}
                errors={errors}
                pattern="hiraganaOrKatakana"
              />
            )
          case 'email':
            return (
              <TextInputField<FormType>
                {...fieldProps}
                name="email"
                type="email"
                pattern="email"
                error={errors['email']}
                required
              />
            )
          case 'phone_number':
            return (
              <TextInputField<FormType>
                {...fieldProps}
                name="phoneNumber"
                pattern="phoneNumber"
                error={errors['phoneNumber']}
              />
            )
          case 'gender':
            const genderOptions = [
              { label: '女性', value: 'female' },
              { label: '男性', value: 'male' },
            ]
            if (!item.isRequired) {
              genderOptions.push({ label: 'その他', value: 'other' })
            }
            return (
              <RadioInputField<FormType>
                {...fieldProps}
                name="gender"
                radioOptions={genderOptions}
                error={errors['gender']}
                disabled={false}
              />
            )
          case 'number_of_people':
            return (
              <SingleSelectField
                {...fieldProps}
                name="numberOfPeople"
                options={[
                  {
                    value: 1,
                    label: '1人',
                  },
                  {
                    value: 2,
                    label: '2人',
                  },
                  {
                    value: 3,
                    label: '3人',
                  },
                  {
                    value: 4,
                    label: '4人',
                  },
                ]}
                error={errors['numberOfPeople']}
              />
            )
          case 'content':
            return <TextareaField<FormType> {...fieldProps} name="content" error={errors['content']} rows={4} />
          case 'reservation_item':
            switch (item.reservationItem.inputType) {
              case 'string':
                return (
                  <TextInputField<FormType>
                    {...fieldProps}
                    name={`reservationInfos.${item.reservationItem?.id}`}
                    error={errors['reservationInfos'] ? errors['reservationInfos'][item.reservationItem.id] : null}
                    type="text"
                  />
                )
              case 'text':
                return (
                  <TextareaField<FormType>
                    {...fieldProps}
                    name={`reservationInfos.${item.reservationItem.id}`}
                    error={errors['reservationInfos'] ? errors['reservationInfos'][item.reservationItem.id] : null}
                  />
                )
              case 'select':
                return (
                  <SingleSelectField
                    {...fieldProps}
                    name={`reservationInfos.${item.reservationItem.id}`}
                    error={errors['reservationInfos'] ? errors['reservationInfos'][item.reservationItem.id] : null}
                    options={
                      item.reservationItem.selectItems?.map((selectItem) => ({
                        value: selectItem,
                        label: selectItem,
                      })) || []
                    }
                  />
                )
              case 'integer':
                return (
                  <NumberInputField<FormType>
                    {...fieldProps}
                    name={`reservationInfos.${item.reservationItem.id}`}
                    error={errors['reservationInfos'] ? errors['reservationInfos'][item.reservationItem.id] : null}
                  />
                )
              case 'date':
                return (
                  <DateInputField
                    {...fieldProps}
                    name={`reservationInfos.${item.reservationItem.id}`}
                    error={errors['reservationInfos'] ? errors['reservationInfos'][item.reservationItem.id] : null}
                  />
                )
              case 'month':
                return (
                  <MonthInputField
                    {...fieldProps}
                    name={`reservationInfos.${item.reservationItem.id}`}
                    error={errors['reservationInfos'] ? errors['reservationInfos'][item.reservationItem.id] : null}
                  />
                )
              case 'multi_select':
                return (
                  <MultiSelectField
                    {...fieldProps}
                    name={`reservationInfos.${item.reservationItem.id}`}
                    error={errors['reservationInfos'] ? errors['reservationInfos'][item.reservationItem.id] : null}
                    options={
                      item.reservationItem.selectItems?.map((selectItem) => ({
                        value: selectItem,
                        label: selectItem,
                      })) || []
                    }
                  />
                )
              default:
                return null
            }
          default:
            return null
        }
      })}
      <div className={`${className} agreement`}>
        <CheckBox
          linkText="プライバシーポリシー"
          linkURL={privacyPolicyLink}
          label={`${privacyPolicyLink ? '' : 'プライバシーポリシー'}への同意`}
          checked={isCheckedAgreement}
          onChange={(e) => setIsCheckedAgreement(e.target.checked)}
          disabled={disabled}
        />
      </div>
      <div className="submit">
        <Button type="submit" color={primaryColor} disabled={disabled || !isCheckedAgreement}>
          送信する
        </Button>
      </div>
    </form>
  )
}

const StyledComponent = styled(Component)`
  display: grid;
  justify-content: center;
  &.form {
    /* NOTE: gridを使うと〇〇Fieldのlabelとinputが何故か折り返してしまうためflexとする */
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media (max-width: 751px) {
      gap: 12px;
    }
  }

  > .submit {
    @media (min-width: 520px) {
      min-width: 400px;
      margin: 0 auto;
    }
  }
`
